// ============================================================================
// MediaHallucinations — Painel de suspeitas de alucinação de mídia
// ----------------------------------------------------------------------------
// Mostra respostas da Syra que falaram de áudio/transcrição/foto/vídeo SEM
// que houvesse mídia hidratada (status='processed') no mesmo grupo na janela
// de ±10 minutos. Usa duas views:
//   • v_media_hallucination_by_group_7d  → frequência por grupo (cards/tabela)
//   • v_media_hallucination_suspects     → lista de casos individuais
// ============================================================================

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertTriangle, RefreshCw, Mic, Image as ImageIcon, Video, FileQuestion, ShieldCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type GroupRow = {
  org_id: string;
  group_id: string | null;
  group_name: string | null;
  suspects_total: number;
  suspects_audio: number;
  suspects_image: number;
  suspects_video: number;
  suspects_other: number;
  mentions_total: number;
  suspect_rate_pct: number | null;
  last_suspect_at: string | null;
};

type SuspectRow = {
  message_id: string;
  org_id: string;
  group_id: string | null;
  created_at: string;
  claimed_kind: "audio" | "image" | "video" | "other";
  snippet: string;
  has_hydrated_media: boolean;
  has_open_clarification: boolean;
};

const KindIcon = ({ kind }: { kind: SuspectRow["claimed_kind"] }) => {
  const cls = "w-3.5 h-3.5";
  if (kind === "audio") return <Mic className={cls} />;
  if (kind === "image") return <ImageIcon className={cls} />;
  if (kind === "video") return <Video className={cls} />;
  return <FileQuestion className={cls} />;
};

export default function MediaHallucinations() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [suspects, setSuspects] = useState<SuspectRow[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, s] = await Promise.all([
        supabase
          .from("v_media_hallucination_by_group_7d" as any)
          .select("*")
          .order("suspects_total", { ascending: false })
          .limit(50),
        supabase
          .from("v_media_hallucination_suspects" as any)
          .select("*")
          .eq("has_hydrated_media", false)
          .order("created_at", { ascending: false })
          .limit(100),
      ]);
      if (g.error) throw g.error;
      if (s.error) throw s.error;
      setGroups((g.data || []) as unknown as GroupRow[]);
      setSuspects((s.data || []) as unknown as SuspectRow[]);
    } catch (e: any) {
      setError(e?.message || "Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = useMemo(() => {
    return groups.reduce(
      (acc, g) => {
        acc.suspects += g.suspects_total || 0;
        acc.audio += g.suspects_audio || 0;
        acc.image += g.suspects_image || 0;
        acc.video += g.suspects_video || 0;
        acc.mentions += g.mentions_total || 0;
        return acc;
      },
      { suspects: 0, audio: 0, image: 0, video: 0, mentions: 0 }
    );
  }, [groups]);

  const filteredSuspects = useMemo(
    () => (selectedGroup ? suspects.filter((s) => s.group_id === selectedGroup) : suspects),
    [suspects, selectedGroup]
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-destructive" />
            Suspeitas de alucinação de mídia
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Respostas da Syra que mencionam áudio/transcrição/foto/vídeo sem que exista mídia
            hidratada (<code className="text-xs">status=processed</code>) no mesmo grupo na janela
            de ±10 minutos. Heurística — falsos positivos são possíveis.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </header>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Suspeitas (7d)" value={totals.suspects} tone="destructive" />
        <KpiCard label="Áudio" value={totals.audio} icon={<Mic className="w-4 h-4" />} />
        <KpiCard label="Imagem" value={totals.image} icon={<ImageIcon className="w-4 h-4" />} />
        <KpiCard label="Vídeo" value={totals.video} icon={<Video className="w-4 h-4" />} />
        <KpiCard
          label="Total mencionado"
          value={totals.mentions}
          icon={<ShieldCheck className="w-4 h-4" />}
        />
      </div>

      {/* Grupos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Frequência por grupo (últimos 7 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : groups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhuma suspeita detectada nos últimos 7 dias. ✨
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Suspeitas</TableHead>
                    <TableHead className="text-right">Áudio</TableHead>
                    <TableHead className="text-right">Imagem</TableHead>
                    <TableHead className="text-right">Vídeo</TableHead>
                    <TableHead className="text-right">Outros</TableHead>
                    <TableHead className="text-right">Taxa</TableHead>
                    <TableHead>Última</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => {
                    const isSel = selectedGroup === g.group_id;
                    return (
                      <TableRow
                        key={g.group_id ?? "no-group"}
                        className={isSel ? "bg-muted/40" : ""}
                      >
                        <TableCell className="font-medium">
                          {g.group_name || (
                            <span className="text-muted-foreground italic">sem nome</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={g.suspects_total > 0 ? "destructive" : "secondary"}>
                            {g.suspects_total}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{g.suspects_audio}</TableCell>
                        <TableCell className="text-right">{g.suspects_image}</TableCell>
                        <TableCell className="text-right">{g.suspects_video}</TableCell>
                        <TableCell className="text-right">{g.suspects_other}</TableCell>
                        <TableCell className="text-right">
                          {g.suspect_rate_pct != null ? `${g.suspect_rate_pct}%` : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {g.last_suspect_at
                            ? formatDistanceToNow(new Date(g.last_suspect_at), {
                                addSuffix: true,
                                locale: ptBR,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant={isSel ? "default" : "ghost"}
                            size="sm"
                            onClick={() =>
                              setSelectedGroup(isSel ? null : g.group_id)
                            }
                          >
                            {isSel ? "Limpar" : "Ver casos"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Casos individuais */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Casos individuais{" "}
            <span className="text-xs font-normal text-muted-foreground">
              ({filteredSuspects.length}
              {selectedGroup ? " — filtrado por grupo" : " — todos os grupos"})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : filteredSuspects.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhum caso para mostrar.
            </p>
          ) : (
            <ul className="space-y-3">
              {filteredSuspects.map((s) => (
                <li
                  key={s.message_id}
                  className="border rounded-md p-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
                    <Badge variant="outline" className="gap-1">
                      <KindIcon kind={s.claimed_kind} />
                      {s.claimed_kind}
                    </Badge>
                    {s.has_open_clarification && (
                      <Badge variant="secondary">clarification aberta</Badge>
                    )}
                    <span>•</span>
                    <span>
                      {formatDistanceToNow(new Date(s.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </span>
                    <span>•</span>
                    <span className="font-mono truncate">
                      grupo: {s.group_id?.slice(0, 8) ?? "—"}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.snippet}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  tone?: "destructive" | "default";
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          {icon}
          {label}
        </div>
        <div
          className={`text-2xl font-bold mt-1 ${
            tone === "destructive" && value > 0 ? "text-destructive" : ""
          }`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
